import { useMemo, useState } from 'react';

import { TAmenities, TInspection, TSectionInspection } from '../services/Amenities/types';

type Props = {
  inspectionAmenities?: TInspection;
};

export interface ISection {
  sectionKey: string;
  amenities: TAmenities[];
}

export interface UseSearchAmenitiesHook {
  activeTab: 0 | 1;
  searchTerm: string;
  sections: ISection[];
  handleSearchTermChange: (newTerm: string) => void;
  handleTabChange: (tabIndex: number) => void;
}

const useSearchAmenities = ({ inspectionAmenities }: Props): UseSearchAmenitiesHook => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const sections = useMemo<ISection[]>(() => {
    if (!inspectionAmenities) return [];

    return Object.entries(inspectionAmenities).flatMap(([sectionKey, sectionValue]) => {
      if (
        sectionKey === 'updated_at' ||
        typeof sectionValue !== 'object' ||
        sectionValue === null
      ) {
        return [];
      }

      const sec = sectionValue as TSectionInspection;
      const rawAmenities = activeTab === 0 ? sec.address?.amenities : sec.listing?.amenities;

      if (!Array.isArray(rawAmenities) || rawAmenities.length === 0) {
        return [];
      }

      const filtered = rawAmenities.filter((a) =>
        a.item_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      if (filtered.length === 0) {
        return [];
      }

      return [{ sectionKey, amenities: filtered }];
    });
  }, [inspectionAmenities, searchTerm, activeTab]);

  const handleSearchTermChange = (newTerm: string) => {
    setSearchTerm(newTerm);
  };

  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex === 0 ? 0 : 1);
  };

  return {
    activeTab,
    searchTerm,
    sections,
    handleSearchTermChange,
    handleTabChange,
  };
};

export default useSearchAmenities;
